import React from 'react';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { CharterFareInventory, CharterInventoryFlight } from '~/components/Flights/Charter/charter.types';

import {
  availableSeats,
  blockedSeats,
  cabinLabel,
  formatDate,
  formatFlightNumber,
  formatPrice,
  formatTime,
} from './charterInventoryUtils';

export type InventoryTableRow = {
  stayDuration?: number;
  flight: CharterInventoryFlight;
  fare: CharterFareInventory;
  uniqueKey?: string;
};

type InventoryTableProps = {
  rows: Array<InventoryTableRow | null>; // null indicates empty row for spacing
  showStayDuration?: boolean;
  emptyMessage: string;
};

export default function InventoryTable({ rows, showStayDuration, emptyMessage }: InventoryTableProps) {
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1600, '& .MuiTableCell-root': { minWidth: 100 } }}>
        <TableHead>
          <TableRow>
            {showStayDuration ? <TableCell>Stay (nights)</TableCell> : null}
            <TableCell>Departure Date</TableCell>
            <TableCell>Departure Time</TableCell>
            <TableCell>Arrival Date</TableCell>
            <TableCell>Arrival Time</TableCell>
            <TableCell>Departure Airport Code</TableCell>
            <TableCell>Arrival Airport Code</TableCell>
            <TableCell>Flight Number</TableCell>
            <TableCell>Cabin Class</TableCell>
            <TableCell>Booking Class</TableCell>
            <TableCell>Total Bookable Seats</TableCell>
            <TableCell>Sold Seats</TableCell>
            <TableCell>Blocked Seats</TableCell>
            <TableCell>Available Seats</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Seat Price (Adult)</TableCell>
            <TableCell>Tax (Adult)</TableCell>
            <TableCell>Seat Price (Child)</TableCell>
            <TableCell>Tax (Child)</TableCell>
            <TableCell>Seat Price (Infant)</TableCell>
            <TableCell>Tax (Infant)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            if (!row) {
              // Render empty row for spacing
              return (
                <TableRow key={`empty-${index}`} sx={{ height: 64 }}>
                  <TableCell colSpan={showStayDuration ? 21 : 20} />
                </TableRow>
              );
            }

            return (
              <TableRow
                key={
                  row.uniqueKey ??
                  `${row.stayDuration ?? 'depart'}-${row.flight.flightInstanceId}-${row.fare.bookingClass}`
                }
                sx={{ height: 64 }}
              >
                {showStayDuration ? <TableCell>{row.stayDuration}</TableCell> : null}
                <TableCell>{formatDate(row.flight.depDate)}</TableCell>
                <TableCell>{formatTime(row.flight.depTime)}</TableCell>
                <TableCell>{formatDate(row.flight.arrDate)}</TableCell>
                <TableCell>{formatTime(row.flight.arrTime)}</TableCell>
                <TableCell>{row.flight.depAirport}</TableCell>
                <TableCell>{row.flight.arrAirport}</TableCell>
                <TableCell>{formatFlightNumber(row.flight.flightNumber, row.flight.airlineCode)}</TableCell>
                <TableCell>{cabinLabel[row.fare.cabinClass] ?? row.fare.cabinClass}</TableCell>
                <TableCell>{row.fare.bookingClass}</TableCell>
                <TableCell>{row.fare.allocation}</TableCell>
                <TableCell>{row.fare.booked}</TableCell>
                <TableCell>{blockedSeats(row.flight, row.fare)}</TableCell>
                <TableCell>{availableSeats(row.fare)}</TableCell>
                <TableCell>{row.fare.currency ?? ''}</TableCell>
                <TableCell>{formatPrice(row.fare, 'priceAdult')}</TableCell>
                <TableCell>{formatPrice(row.fare, 'taxAdult')}</TableCell>
                <TableCell>{formatPrice(row.fare, 'priceChild')}</TableCell>
                <TableCell>{formatPrice(row.fare, 'taxChild')}</TableCell>
                <TableCell>{formatPrice(row.fare, 'priceInfant')}</TableCell>
                <TableCell>{formatPrice(row.fare, 'taxInfant')}</TableCell>
              </TableRow>
            );
          })}
          {!rows.length ? (
            <TableRow>
              <TableCell colSpan={showStayDuration ? 21 : 20} align="center" sx={{ height: 64 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
